import { Client, cacheExchange, fetchExchange } from '@urql/core';
import { graphql } from './gql';

export class VotesIndexer {
  private client: Client;
  public readonly url: string;
  public readonly logger: typeof console;

  constructor(url: string, logger: typeof console = console) {
    this.url = url;
    this.logger = logger;
    this.client = new Client({
      url: url,
      exchanges: [cacheExchange, fetchExchange],
      requestPolicy: 'network-only',
    });
  }

  public async getNumberOfSolutionsVotesPerRewardPeriod({
    operatorId,
    solutionsNamespaces,
    rewardPeriod,
  }: {
    operatorId: string;
    solutionsNamespaces: string[];
    rewardPeriod: number;
  }) {
    try {
      const solutionsVotes = await Promise.all(
        solutionsNamespaces.map(async (solutionNamespace) => {
          return this.getNumberOfSolutionVotesPerRewardPeriod({
            operatorId,
            solutionNamespace,
            rewardPeriod,
          });
        }),
      );
      const solutionGroupVotes = solutionsVotes.reduce((acc, curr) => acc + curr, 0);
      return solutionGroupVotes;
    } catch (e) {
      this.logger.error('getSolutionsVotesPerRewardPeriod', { e });
      return 0;
    }
  }

  private async getNumberOfSolutionVotesPerRewardPeriod({
    operatorId,
    solutionNamespace,
    rewardPeriod,
  }: {
    operatorId: string;
    solutionNamespace: string;
    rewardPeriod: number;
  }) {
    try {
      const query = graphql(`
        query GetSolutionVotesPerRewardPeriod(
          $operatorId: String!
          $solutionNamespace: String!
          $rewardPeriod: Float!
        ) {
          operatorNumberOfSubmissions(
            operatorId: $operatorId
            rewardPeriodIndex: $rewardPeriod
            solutionNamespace: $solutionNamespace
          ) {
            numberOfSubmissions
          }
        }
      `);

      const result = await this.client.query(query, {
        operatorId,
        solutionNamespace,
        rewardPeriod,
      });
      const votes = result?.data?.operatorNumberOfSubmissions?.numberOfSubmissions;
      return votes || 0;
    } catch (e) {
      this.logger.error('getSolutionVotesPerRewardPeriod', { e });
      return 0;
    }
  }

  public async getTransactionsByPeriod({
    operatorId,
    solutionId,
    rewardPeriod,
  }: {
    operatorId: string;
    solutionId: string;
    rewardPeriod: number;
  }) {
    try {
      const query = graphql(`
        query GetTransactionsByPeriod(
          $operatorId: String!
          $solutionId: String!
          $rewardPeriod: Int!
        ) {
          operatorById(id: $operatorId) {
            votes(
              where: { rewardPeriod: { index_eq: $rewardPeriod }, solution: { id_eq: $solutionId } }
            ) {
              blockHash
              extrinsicHash
              blockNumber
              rewardPeriod {
                index
              }
              result {
                result
              }
              votingRound {
                votingRoundId
              }
            }
          }
        }
      `);
      const result = await this.client.query(query, { operatorId, solutionId, rewardPeriod });
      const transactions = result?.data?.operatorById?.votes.map((vote) => ({
        blockHash: vote.blockHash,
        extrinsicHash: vote.extrinsicHash,
        blockNumber: vote.blockNumber,
        rewardPeriod: vote.rewardPeriod?.index,
        result: vote.result?.result,
        successful: true,
        votingRoundId: vote.votingRound?.votingRoundId,
      }));
      return transactions || [];
    } catch (e) {
      this.logger.error('getTransactionsByPeriod', { e });
      return [];
    }
  }

  public async getVotesByPeriod({
    operatorId,
    rewardPeriod,
  }: {
    operatorId: string;
    rewardPeriod: number;
  }) {
    try {
      const query = graphql(`
        query GetVotesByPeriod($operatorId: String!, $rewardPeriod: Int!) {
          operatorById(id: $operatorId) {
            votes(where: { rewardPeriod: { index_eq: $rewardPeriod } }) {
              blockHash
              extrinsicHash
              blockNumber
              rewardPeriod {
                index
              }
              solution {
                id
              }
              result {
                result
              }
              votingRound {
                votingRoundId
              }
            }
          }
        }
      `);
      const result = await this.client.query(query, { operatorId, rewardPeriod });
      const transactions = result?.data?.operatorById?.votes.map((vote) => ({
        blockHash: vote.blockHash,
        extrinsicHash: vote.extrinsicHash,
        blockNumber: vote.blockNumber,
        rewardPeriod: vote.rewardPeriod?.index,
        result: vote.result?.result,
        successful: true,
        solutionId: vote.solution?.id,
        votingRoundId: vote.votingRound?.votingRoundId,
      }));
      return transactions || [];
    } catch (e) {
      this.logger.error('getVotesByPeriod', { e });
      return [];
    }
  }

  public async getVotingGraphData({
    operatorId,
    solutionNamespace,
  }: {
    operatorId: string;
    solutionNamespace: string;
  }) {
    try {
      const query = graphql(`
        query GetVotingGraphData($operatorId: String!, $solutionNamespace: String!) {
          operatorById(id: $operatorId) {
            votes(where: { solution: { id_eq: $solutionNamespace } }) {
              rewardPeriod {
                index
              }
              result {
                result
              }
            }
          }
        }
      `);

      const result = await this.client.query(query, { operatorId, solutionNamespace });
      const parsedResults = result?.data?.operatorById?.votes.map((vote) => ({
        rewardPeriod: vote.rewardPeriod?.index,
        result: vote.result?.result,
        successful: true,
      }));
      return parsedResults || [];
    } catch (e) {
      this.logger.error('getVotingGraphData', { e });
      return [];
    }
  }
}
